import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { subjectColor } from "@/lib/constants";
import { HelpCircle, Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";

const DIFF = { kolay: "Kolay", orta: "Orta", zor: "Zor" };
const GRADES = ["5", "6", "7", "8"];

export default function Questions() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [curriculum, setCurriculum] = useState({});
  const [topicGrade, setTopicGrade] = useState("8");
  const [manualTopic, setManualTopic] = useState(false);

  const load = () => {
    api.get(`/exams/${examId}`).then((r) => setExam(r.data));
    api.get(`/exams/${examId}/questions`).then((r) => setQuestions(r.data));
  };
  useEffect(() => { load(); api.get("/curriculum").then((r) => setCurriculum(r.data)); /* eslint-disable-next-line */ }, [examId]);

  const topicList = useMemo(() => (curriculum[topicGrade] || {})[editing?.subject_key] || [], [curriculum, topicGrade, editing?.subject_key]);
  const topicNames = useMemo(() => topicList.map((o) => o.t), [topicList]);

  const subjectRanges = useMemo(() => {
    if (!exam) return [];
    let run = 0;
    return [...exam.subjects].sort((a, b) => a.order - b.order).map((s) => {
      const r = { ...s, start: run + 1, end: run + s.question_count };
      run += s.question_count;
      return r;
    });
  }, [exam]);

  const empty = { number: 1, subject_key: "", topic: "", kazanim: "", text: "", option_a: "", option_b: "", option_c: "", option_d: "", correct: "A", difficulty: "orta" };

  const openNew = () => { setTopicGrade(GRADES.includes(exam?.grade) ? exam.grade : "8"); setManualTopic(false); setEditing({ ...empty, subject_key: exam?.subjects?.[0]?.key || "" }); setOpen(true); };
  const openEdit = (q) => {
    const g = GRADES.includes(exam?.grade) ? exam.grade : "8";
    const list = ((curriculum[g] || {})[q.subject_key] || []).map((o) => o.t);
    setTopicGrade(g);
    setManualTopic(!!(q.topic && !list.includes(q.topic)));
    setEditing({ ...q }); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...editing, number: parseInt(editing.number) };
      if (editing.id) await api.put(`/questions/${editing.id}`, payload);
      else await api.post(`/exams/${examId}/questions`, payload);
      toast.success("Soru kaydedildi");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/questions/${id}`); toast.success("Soru silindi"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!exam) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => nav("/sinavlar")} className="mb-2 -ml-2"><ArrowLeft className="h-4 w-4 mr-1" />Sınavlar</Button>
      <PageHeader title={`Soru Yönetimi — ${exam.name}`} desc={`${exam.total_questions} soruluk sınav · ${questions.length} soru girildi`}>
        <Button onClick={openNew} data-testid="question-add"><Plus className="h-4 w-4 mr-2" />Yeni Soru</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        {subjectRanges.map((s) => (
          <Badge key={s.key} className={subjectColor(s.key).badge}>{s.name}: {s.start}–{s.end}</Badge>
        ))}
      </div>

      {questions.length === 0 ? (
        <EmptyState icon={HelpCircle} title="Henüz soru girilmedi" desc="Soru metni ve kazanımları buradan yönetebilirsiniz. (Soru metni isteğe bağlıdır; puanlama cevap anahtarına göre yapılır.)">
          <Button onClick={openNew}>Yeni Soru Ekle</Button>
        </EmptyState>
      ) : (
        <Card className="bg-card border-border overflow-hidden">
          <div className="overflow-x-auto ktt-scroll">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3 w-14">No</th><th className="text-left p-3">Ders</th>
                  <th className="text-left p-3">Konu / Kazanım</th><th className="text-left p-3 w-20">Doğru</th>
                  <th className="text-left p-3 w-20">Zorluk</th><th className="p-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q.id} className="border-t border-border hover:bg-muted/30" data-testid={`question-row-${q.number}`}>
                    <td className="p-3 font-mono font-semibold">{q.number}</td>
                    <td className="p-3"><Badge className={subjectColor(q.subject_key).badge}>{exam.subjects.find((s) => s.key === q.subject_key)?.name || q.subject_key}</Badge></td>
                    <td className="p-3"><div className="font-medium">{q.topic || "—"}</div><div className="text-xs text-muted-foreground font-mono">{q.kazanim}</div></td>
                    <td className="p-3 font-mono font-bold">{q.correct}</td>
                    <td className="p-3 text-xs">{DIFF[q.difficulty] || q.difficulty}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(q)} data-testid={`question-edit-${q.number}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(q.id)} data-testid={`question-delete-${q.number}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto ktt-scroll">
          <DialogHeader><DialogTitle>{editing?.id ? "Soru Düzenle" : "Yeni Soru"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Soru No</Label><Input type="number" value={editing.number} onChange={(e) => setEditing({ ...editing, number: e.target.value })} data-testid="q-number" /></div>
                <div className="space-y-1.5"><Label>Ders</Label>
                  <Select value={editing.subject_key} onValueChange={(v) => setEditing({ ...editing, subject_key: v })}>
                    <SelectTrigger data-testid="q-subject"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{exam.subjects.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Konu</Label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Sınıf</span>
                    <Select value={topicGrade} onValueChange={(v) => { setTopicGrade(v); setManualTopic(false); }}>
                      <SelectTrigger className="h-7 w-16" data-testid="q-topic-grade"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{["5", "6", "7", "8"].map((g) => <SelectItem key={g} value={g}>{g}. sınıf</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {manualTopic || topicList.length === 0 ? (
                  <div className="space-y-1">
                    <Input value={editing.topic} onChange={(e) => setEditing({ ...editing, topic: e.target.value })} placeholder="Konuyu elle yazın" data-testid="q-topic-input" />
                    {topicList.length > 0 && <button type="button" className="text-xs text-primary hover:underline" onClick={() => setManualTopic(false)} data-testid="q-topic-pick">↩ müfredattan seç</button>}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Select value={topicNames.includes(editing.topic) ? editing.topic : ""} onValueChange={(v) => { if (v === "__manual__") { setManualTopic(true); setEditing({ ...editing, topic: "" }); } else { const o = topicList.find((x) => x.t === v); setEditing({ ...editing, topic: v, kazanim: o?.k || editing.kazanim }); } }}>
                      <SelectTrigger data-testid="q-topic"><SelectValue placeholder="Müfredattan konu seçin" /></SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-72">
                        {topicList.map((o) => <SelectItem key={o.t} value={o.t}>{o.t}</SelectItem>)}
                        <SelectItem value="__manual__">✏️ Elle gireceğim…</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-1.5"><Label>Kazanım</Label><Input value={editing.kazanim} onChange={(e) => setEditing({ ...editing, kazanim: e.target.value })} placeholder="örn. M.8.1.2.1" data-testid="q-kazanim" /></div>
              </div>
              <div className="space-y-1.5"><Label>Soru Metni</Label><Textarea rows={2} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                {["a", "b", "c", "d"].map((o) => (
                  <div key={o} className="space-y-1.5"><Label>{o.toUpperCase()} Şıkkı</Label><Input value={editing[`option_${o}`]} onChange={(e) => setEditing({ ...editing, [`option_${o}`]: e.target.value })} /></div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Doğru Cevap</Label>
                  <Select value={editing.correct} onValueChange={(v) => setEditing({ ...editing, correct: v })}>
                    <SelectTrigger data-testid="q-correct"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{(exam.options || ["A", "B", "C", "D"]).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Zorluk</Label>
                  <Select value={editing.difficulty} onValueChange={(v) => setEditing({ ...editing, difficulty: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{Object.entries(DIFF).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="q-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
